import { Box, Flex, useClipboard } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalBody,
  ModalHeaderDefault,
  Notification,
  useSnackbar,
} from 'ui-components';
import { ContentCaption } from '~/components/ContentCaption';
import {
  Actions,
  Asset,
  AssetDescription,
  CONTENT_DETAIL_MODAL_DEFAULT_TRACK_ID,
  CONTENT_DETAIL_MODAL_TRACK_ID,
  ContentDetailModalTrackIdType,
  Header,
} from '~/components/ContentDetailModal';
import {
  FEATURE_FLAGS,
  INSTAGRAM_CONTENT_TYPES,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import { useGetContent } from '~/hooks/api/useGetContent';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import type {
  IApiSearchInstagramContent,
  ISearchContentCreator,
} from '~/searchApi';

import { ContentDetailMetrics } from '../ContentDetailMetrics';
import { PLATFORM_METRICS } from '../ContentDetailMetrics/ContentDetailMetrics.constants';

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  onNavigation?: (direction: string) => void;
  trackIds?: ContentDetailModalTrackIdType;
  id?: string;
  isMediaKit?: boolean;
}

export const ContentDetailModal = ({
  isOpen,
  onClose,
  onNavigation,
  trackIds: overrideDefaultTrackIds,
  id,
  isMediaKit = false,
}: Props) => {
  const router = useRouter();
  // If there's id (id of the content) use it, if not get it from the query
  const contentId = id ? id : (router.query.contentId as string);
  const snackbar = useSnackbar();

  const { data } = useGetContent(contentId);

  const isMetricsCollaborationFFEnbled = useFeature(
    FEATURE_FLAGS.foamMetricsCollaborations,
  ).isEnabled;

  const content = data?.data[0];

  const { onCopy } = useClipboard(content?.shareLink ?? '');

  const hasNavigation = Boolean(onNavigation);

  const trackIds = useMemo(
    () => ({
      ...CONTENT_DETAIL_MODAL_DEFAULT_TRACK_ID,
      ...overrideDefaultTrackIds,
    }),
    [overrideDefaultTrackIds],
  );

  const handleNext = () => onNavigation && onNavigation('next');
  const handlePrevious = () => onNavigation && onNavigation('previous');

  const handleShare = useCallback(() => {
    onCopy();
    snackbar.success({
      description: 'Link successfully copied',
    });
  }, [onCopy, snackbar]);

  const getMetrics = useCallback(() => {
    const actualPlatform = content?.platform;

    if (actualPlatform === SOCIAL_PLATFORMS.instagram) {
      const contentContainsViews =
        Object.keys(content as IApiSearchInstagramContent).some(
          (key) => key === 'viewsCount',
        ) && Boolean(content?.viewsCount);

      let metrics =
        PLATFORM_METRICS[(content as IApiSearchInstagramContent).channel];

      if (contentContainsViews) {
        if (
          (content as IApiSearchInstagramContent).channel ===
            INSTAGRAM_CONTENT_TYPES.story ||
          (content as IApiSearchInstagramContent).channel ===
            INSTAGRAM_CONTENT_TYPES.post
        ) {
          const { impressions: _impressions, ...rest } = metrics;
          metrics = rest;
        }

        if (
          (content as IApiSearchInstagramContent).channel ===
          INSTAGRAM_CONTENT_TYPES.reels
        ) {
          const {
            igReelsAggregatedAllPlaysCount: _igReelsAggregatedAllPlaysCount,
            ...rest
          } = metrics;
          metrics = rest;
        }
      }
      return metrics;
    }

    return PLATFORM_METRICS[actualPlatform as keyof typeof PLATFORM_METRICS];
  }, [content]);

  useEffect(() => {
    trackEvent(trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.onOpen]);
  }, [contentId, trackIds]);

  const isYoutubeContent = content?.platform === SOCIAL_PLATFORMS.youtube;

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      track-id={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.modal]}
    >
      <ModalHeaderDefault
        title={
          <Header
            hasNavigation={hasNavigation}
            onNext={handleNext}
            onPrevious={handlePrevious}
            creator={content?.creator as ISearchContentCreator}
            trackIds={trackIds}
          />
        }
      />
      <ModalBody>
        {content ? (
          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            gap={{ base: 'spacing-300', md: '0px' }}
          >
            <Flex
              minWidth={
                isYoutubeContent
                  ? { base: 'auto', md: '50%' }
                  : { base: 'auto', md: '280px' }
              }
              paddingRight={{ base: '0', md: '4' }}
              alignItems={'center'}
              flexDir={'column'}
            >
              <Box width={{ base: '70%', md: '100%' }}>
                <Asset
                  content={content}
                  trackId={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.preview]}
                />
                <AssetDescription content={content} trackIds={trackIds} />
              </Box>
            </Flex>
            <Box
              minWidth={
                isYoutubeContent
                  ? { base: 'auto', md: '50%' }
                  : { base: 'auto', md: '448px' }
              }
            >
              {isMediaKit ? (
                <Notification isInline variant="warning" marginBottom={4}>
                  Detailed metrics will not be available when the media kit is
                  shared. In the shared version, it will direct to the platform.
                </Notification>
              ) : null}
              <ContentDetailMetrics
                marginBottom={2}
                content={content}
                metrics={getMetrics()}
                isMetricsCollaborationFFEnbled={isMetricsCollaborationFFEnbled}
              />
              <Actions
                creator={{
                  fullName: content.creator.fullName as string,
                  avatar: content.creator.avatar as string,
                  status: content.creator.status,
                }}
                content={content}
                onShare={handleShare}
                trackIds={trackIds}
              />
              <ContentCaption content={content} />
            </Box>
          </Flex>
        ) : null}
      </ModalBody>
    </Modal>
  );
};
